import { useState, useMemo } from "react";
import { CaretDown, Check } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";

/**
 * Buscador de cuentas contables con búsqueda por código o nombre.
 * Props:
 *   value: código seleccionado (string) o ""
 *   onChange: (codigo) => void
 *   cuentas: lista de cuentas (imputables, se filtra)
 *   placeholder, testid
 */
export default function CuentaCombobox({
  value,
  onChange,
  cuentas,
  placeholder = "Elegir cuenta contable…",
  testid,
  className,
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = cuentas.find((c) => c.codigo === value);

  const norm = (s) =>
    (s || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, ""); // saca tildes

  const filtered = useMemo(() => {
    const q = norm(query).trim();
    if (!q) return cuentas.slice(0, 100);
    const tokens = q.split(/\s+/).filter(Boolean);
    return cuentas
      .filter((c) => {
        const hay = norm(`${c.codigo} ${c.nombre}`);
        return tokens.every((t) => hay.includes(t));
      })
      .slice(0, 100);
  }, [cuentas, query]);

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={cn(
            "w-full flex items-center justify-between gap-2 rounded-sm border border-input bg-white px-3 py-1.5 text-left text-[12px] hover:bg-neutral-50 transition-colors duration-150",
            compact ? "h-8" : "h-9",
            className
          )}
        >
          {selected ? (
            <span className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-[#6B1F24] flex-shrink-0">{selected.codigo}</span>
              <span className="truncate text-[#1A1A1A]">{selected.nombre}</span>
            </span>
          ) : (
            <span className="text-[#999]">{placeholder}</span>
          )}
          <CaretDown size={12} className="text-[#999] flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[92vw] sm:w-[420px] sm:max-w-[420px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            data-testid={testid ? `${testid}-input` : undefined}
            placeholder="Buscar por código o nombre…"
            className="h-9"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList className="max-h-72">
            <CommandEmpty>Sin resultados.</CommandEmpty>
            <CommandGroup>
              {filtered.map((c) => (
                <CommandItem
                  key={c.codigo}
                  value={c.codigo}
                  onSelect={() => {
                    onChange(c.codigo);
                    setOpen(false);
                    setQuery("");
                  }}
                  className="text-[12px] flex items-center gap-2"
                  data-testid={testid ? `${testid}-item-${c.codigo}` : undefined}
                >
                  <Check
                    size={12}
                    className={cn(
                      "flex-shrink-0",
                      value === c.codigo ? "opacity-100 text-[#6B1F24]" : "opacity-0"
                    )}
                  />
                  <span className="font-mono text-[#6B1F24] flex-shrink-0">{c.codigo}</span>
                  <span className="truncate">{c.nombre}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
